import { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '@/components/Animations';
import { ArrowRight, UtensilsCrossed, Cake, ChefHat, Coffee, Heart, Users, ClipboardList, Package, Check } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_gourmet-evolved/artifacts/ukm9lnyj_SANS%20FOND%20V3.png';
const FOOD_1 = 'https://images.unsplash.com/photo-1663530761401-15eefb544889?w=1200&h=800&fit=crop&q=90';
const FOOD_2 = 'https://images.unsplash.com/photo-1621494268492-d01b98eba7e4?w=1000&h=800&fit=crop&q=90';
const FOOD_3 = 'https://images.unsplash.com/photo-1621618625640-c8acf9330149?w=800&h=600&fit=crop&q=90';
const DISCOVER_IMG = 'https://static.wixstatic.com/media/11062b_5f32ce5aac4c45d4ab2001a54e26a7b6f000.jpg/v1/fill/w_600,h_400,al_c,q_80/11062b_5f32ce5aac4c45d4ab2001a54e26a7b6f000.jpg';
const NEWSLETTER_IMG = 'https://static.wixstatic.com/media/11062b_2b292568b4cb4d4cb4292a58d766d068~mv2.jpg/v1/fill/w_600,h_400,al_c,q_80/11062b_2b292568b4cb4d4cb4292a58d766d068~mv2.jpg';

const serviceHighlights = [
  { icon: ChefHat, label: 'Mets cuisinés' },
  { icon: Package, label: 'Boîtes à lunch' },
  { icon: UtensilsCrossed, label: 'Bouchées' },
  { icon: Cake, label: 'Pâtisseries' },
  { icon: Coffee, label: 'Comptoir lunch' },
];

const values = [
  { icon: Heart, text: 'Du contact avec le client' },
  { icon: ClipboardList, text: "À l'élaboration des menus" },
  { icon: ChefHat, text: 'Dans la préparation des mets cuisinés' },
  { icon: Package, text: "Jusqu'à la réception de votre commande" },
];

function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/newsletter`, { email });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
    setLoading(false);
  };

  if (status === 'success') {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-3 px-5 py-4 rounded-xl" style={{ backgroundColor: 'rgba(109,174,101,0.1)', border: '1px solid rgba(109,174,101,0.2)' }}>
        <div className="w-8 h-8 rounded-full bg-[#6DAE65] flex items-center justify-center flex-shrink-0">
          <Check size={16} className="text-white" />
        </div>
        <p className="text-sm font-medium" style={{ color: '#6DAE65' }}>Merci ! Vous etes maintenant inscrit.</p>
      </motion.div>
    );
  }

  return (
    <form className="flex gap-3" onSubmit={handleSubmit}>
      <input type="email" required placeholder="Inscrire votre courriel ici" data-testid="newsletter-email"
        value={email} onChange={e => setEmail(e.target.value)}
        className="flex-1 px-4 py-3 rounded-xl text-base outline-none"
        style={{ backgroundColor: 'var(--color-muted)', border: '1px solid var(--color-border)', color: 'var(--color-fg)' }} />
      <button type="submit" disabled={loading} data-testid="newsletter-submit"
        className="px-6 py-3 rounded-xl text-white font-semibold transition-all duration-300 hover:scale-105 disabled:opacity-50"
        style={{ backgroundColor: 'var(--color-primary)' }}>
        {loading ? '...' : "S'inscrire"}
      </button>
    </form>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { isDark } = useTheme();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* Hero — Modern Split Layout */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden" data-testid="hero-section"
        style={{ background: isDark
          ? 'linear-gradient(160deg, #0c0f0b 0%, #141a12 40%, #1a1210 70%, #0f0d0b 100%)'
          : 'linear-gradient(160deg, #FAF9F6 0%, #F5F0EB 40%, #F0EAE0 70%, #FAF9F6 100%)'
        }}
      >
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px',
        }} />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[150px]" style={{ background: '#F78A7D', opacity: isDark ? 0.15 : 0.08 }} />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full blur-[150px]" style={{ background: '#6DAE65', opacity: isDark ? 0.10 : 0.06 }} />

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-28 sm:pt-32 pb-16 sm:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-6 items-center min-h-[calc(100vh-10rem)]">
            <div className="space-y-8 lg:pr-8">
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.1 }}>
                <img src={LOGO_URL} alt="Concept Gourmet" className="h-24 sm:h-28 w-auto mb-2" />
              </motion.div>

              <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{
                  background: isDark ? 'rgba(109,174,101,0.12)' : 'rgba(109,174,101,0.08)',
                  border: isDark ? '1px solid rgba(109,174,101,0.25)' : '1px solid rgba(109,174,101,0.2)',
                }}>
                  <div className="w-2 h-2 rounded-full bg-[#6DAE65] animate-pulse" />
                  <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#6DAE65]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Région de Portneuf
                  </span>
                </div>
              </motion.div>

              <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4 }}
                className="text-[2.75rem] sm:text-5xl lg:text-[3.5rem] xl:text-[4rem] leading-[1.08] tracking-tight"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 300, color: isDark ? '#FFFFFF' : '#1A2318' }}
              >
                Concept Gourmet est un service de traiteur situé dans la belle région de{' '}
                <span className="relative inline-block">
                  <span style={{ color: '#F78A7D', fontWeight: 500 }}>Portneuf</span>
                  <motion.span className="absolute -bottom-1 left-0 h-[2px] rounded-full" style={{ background: 'linear-gradient(90deg, #F78A7D, #6DAE65)' }}
                    initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 1.2, delay: 1.5 }} />
                </span> !
              </motion.h1>

              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
                className="text-base sm:text-lg leading-relaxed max-w-lg"
                style={{ fontFamily: 'Outfit, sans-serif', color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(26,35,24,0.6)' }}
              >
                Nous sommes spécialisés dans la préparation de mets cuisinés faits maison en formule prêt-à-manger. Chez Concept Gourmet, nous mettons notre coeur dans nos recettes !
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.1 }}
                className="flex flex-wrap items-center gap-4 pt-2"
              >
                <Link to="/services" data-testid="hero-cta"
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-white font-semibold text-base transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl group"
                  style={{ background: 'linear-gradient(135deg, #F78A7D, #E8665A)', boxShadow: '0 4px 30px rgba(247,138,125,0.3)' }}
                >
                  Découvrez nos services
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <a href="tel:4182857251"
                  className="inline-flex items-center gap-2 px-6 py-4 rounded-full font-medium text-sm transition-all duration-300"
                  style={{
                    color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(26,35,24,0.7)',
                    border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(26,35,24,0.15)',
                  }}
                >
                  418-285-7251
                </a>
              </motion.div>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.4 }}
                className="flex flex-wrap gap-2 pt-4"
              >
                {serviceHighlights.map((svc, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium" style={{
                    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(26,35,24,0.04)',
                    border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(26,35,24,0.08)',
                    color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(26,35,24,0.45)',
                  }}>
                    <svc.icon size={12} />
                    {svc.label}
                  </span>
                ))}
              </motion.div>
            </div>

            <div className="relative hidden lg:block h-[calc(100vh-12rem)]">
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, delay: 0.5 }}
                className="absolute top-0 right-0 w-[75%] h-[55%]">
                <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                  className="w-full h-full rounded-3xl overflow-hidden shadow-2xl">
                  <img src={FOOD_1} alt="Plat gastronomique" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                </motion.div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.8 }}
                className="absolute bottom-[5%] left-0 w-[55%] h-[48%]">
                <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                  className="w-full h-full rounded-3xl overflow-hidden shadow-2xl">
                  <img src={FOOD_2} alt="Chef en action" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                </motion.div>
              </motion.div>
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 1.1 }}
                className="absolute bottom-[30%] right-[5%] w-[35%] h-[30%]">
                <motion.div animate={{ y: [0, -8, 0], rotate: [0, 1, 0] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
                  className="w-full h-full rounded-2xl overflow-hidden shadow-xl">
                  <img src={FOOD_3} alt="Détail culinaire" className="w-full h-full object-cover" />
                </motion.div>
              </motion.div>
            </div>

            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.6 }}
              className="lg:hidden relative h-64 sm:h-80 rounded-2xl overflow-hidden shadow-2xl">
              <img src={FOOD_1} alt="Plat gastronomique" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Valeurs - L'humain au centre */}
      <section className="py-20 sm:py-28 px-4" data-testid="values-section">
        <div className="max-w-4xl mx-auto text-center">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Toute l'équipe met l'humain au centre du travail à accomplir !
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
            {values.map((v, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div className="flex items-center gap-4 p-5 rounded-2xl transition-all duration-300 hover:-translate-y-1"
                  style={{ backgroundColor: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>
                  <v.icon size={24} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                  <p className="text-base font-medium text-left" style={{ fontFamily: 'Outfit, sans-serif' }}>{v.text}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Découvrez nos services - avec vidéo */}
      <section className="py-20 sm:py-28 px-4" style={{ backgroundColor: 'var(--color-muted)' }} data-testid="services-preview">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-video">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                  poster="https://static.wixstatic.com/media/11062b_5f32ce5aac4c45d4ab2001a54e26a7b6f000.jpg/v1/fill/w_800,h_450,al_c,q_80/11062b_5f32ce5aac4c45d4ab2001a54e26a7b6f000.jpg"
                >
                  <source src="https://video.wixstatic.com/video/11062b_5f32ce5aac4c45d4ab2001a54e26a7b6/720p/mp4/file.mp4" type="video/mp4" />
                </video>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.15}>
              <h2 className="text-3xl sm:text-4xl font-light mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Découvrez nos services offerts
              </h2>
              <Link to="/services" data-testid="discover-services-btn"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-white font-semibold text-base transition-all duration-300 hover:scale-105 group"
                style={{ background: 'linear-gradient(135deg, #F78A7D, #E8665A)', boxShadow: '0 4px 20px rgba(247,138,125,0.3)' }}
              >
                Nos services
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 sm:py-28 px-4" data-testid="newsletter-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <h2 className="text-3xl sm:text-4xl font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Restez informé !
              </h2>
              <p className="text-lg mb-6" style={{ color: 'var(--color-muted-fg)' }}>
                Soyez les premiers à connaître nos promotions et nos nouveautés.
              </p>
              <NewsletterForm />
            </ScrollReveal>
            <ScrollReveal delay={0.15}>
              <img src={NEWSLETTER_IMG} alt="Restez informé" className="w-full rounded-2xl shadow-xl" />
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
